import './App.css';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import React, { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import ForBuyers from './pages/ForBuyers';
import ForSuppliers from './pages/ForSuppliers';
import OurClients from './pages/OurClients';
import Signup from './pages/Signup';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import ClaimProfile from './pages/ClaimProfile';
import Profile from './pages/Profile';
import Directory from './pages/Directory';
import CompanyProfile from './pages/CompanyProfile';
import Matches from './pages/Matches';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import InvestorRelations from './pages/InvestorRelations';
import { supabase } from './lib/supabase';

// Component to handle auth callbacks
function AuthCallbackHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    // Reset the ref when location changes (new verification attempt)
    hasProcessed.current = false;
  }, [location.pathname]);

  useEffect(() => {
    const handleAuthCallback = async () => {
      // Prevent multiple executions
      if (hasProcessed.current) {
        return;
      }

      // Check for auth tokens in hash (SPA) or query (some Supabase redirects put them in query)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);
      const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
      const token = queryParams.get('token');

      // Handle magic link / email verification (tokens in hash or query)
      if (accessToken) {
        hasProcessed.current = true;
        try {
          // Set the session with Supabase
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (error) {
            console.error('Error setting session:', {
              message: error.message,
              status: error.status,
              error,
            });
            // Pass through Supabase error so user sees a helpful message (e.g. link expired)
            const errorParam = new URLSearchParams({ error: 'verification_failed' });
            if (error.message) {
              errorParam.set('message', error.message);
            }
            navigate(`/signup?${errorParam.toString()}`, { replace: true });
            return;
          }

          // Sync user to database (required for claim and other auth flows)
          const { syncUser } = await import('./lib/authSync');
          const syncResult = await syncUser();

          if (syncResult.success && syncResult.data) {
            localStorage.setItem('Id', syncResult.data.userid.toString());
            if (syncResult.data.userType) {
              localStorage.setItem('userType', syncResult.data.userType);
            }
            if (syncResult.data.profile_complete === true) {
              navigate('/directory', { replace: true });
              return;
            }
          }

          // If user was in the claim flow, send them back to the claim page (stored when they left for signup)
          const pendingClaim = localStorage.getItem('pending_claim');
          if (pendingClaim) {
            try {
              const { path } = JSON.parse(pendingClaim);
              localStorage.removeItem('pending_claim');
              if (path && path.startsWith('/claim/')) {
                navigate(path, { replace: true });
                return;
              }
            } catch (e) {
              localStorage.removeItem('pending_claim');
            }
          }

          const pathAndSearch = window.location.pathname + (window.location.search || '');
          const isClaimPage = window.location.pathname.startsWith('/claim/');
          if (isClaimPage) {
            navigate(pathAndSearch, { replace: true });
            return;
          }

          navigate('/onboarding', { replace: true });
        } catch (error) {
          console.error('Error handling auth callback:', error);
          const pendingClaim = localStorage.getItem('pending_claim');
          if (pendingClaim) {
            try {
              const { path } = JSON.parse(pendingClaim);
              localStorage.removeItem('pending_claim');
              if (path && path.startsWith('/claim/')) {
                navigate(path, { replace: true });
                return;
              }
            } catch (e) {
              localStorage.removeItem('pending_claim');
            }
          }
          const pathAndSearch = window.location.pathname + (window.location.search || '');
          const isClaimPage = window.location.pathname.startsWith('/claim/');
          if (isClaimPage) {
            navigate(pathAndSearch, { replace: true });
            return;
          }
          navigate('/onboarding', { replace: true });
        }
        return;
      }

      // Handle token-based verification (if Supabase redirects with token in query)
      // Skip when on claim page: ?token= is the claim token, not a Supabase auth token
      if (token && !window.location.pathname.startsWith('/claim/')) {
        // If we have a token in query params, it might be Supabase's auth callback
        // Try to get the session from Supabase's auth state
        try {
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();

          if (session && !sessionError) {
            // We have a session, clear query params
            window.history.replaceState({}, document.title, window.location.pathname);

            // Sync user to database and check if profile is complete
            const { syncUser } = await import('./lib/authSync');
            const syncResult = await syncUser();

            if (syncResult.success && syncResult.data) {
              localStorage.setItem('Id', syncResult.data.userid.toString());
              if (syncResult.data.userType) {
                localStorage.setItem('userType', syncResult.data.userType);
              }

              // Check if profile is complete - if so, redirect to directory instead of onboarding
              if (syncResult.data.profile_complete === true) {
                navigate('/directory', { replace: true });
                return;
              }
            }

            // Profile not complete or sync failed, redirect to onboarding (it will handle sync if needed)
            navigate('/onboarding', { replace: true });
            return;
          }

          // If no session, the token might need to be verified differently
          // For now, redirect to signup and let the user know they may need to click the link again
          console.warn('Token found in query but no session established. User may need to ensure redirect URL is configured in Supabase dashboard.');
          navigate('/signup?error=verification_failed');
        } catch (error) {
          console.error('Error handling token verification:', error);
          navigate('/signup?error=verification_failed');
        }
        return;
      }
    };

    handleAuthCallback();
  }, [navigate, location]);

  return null;
}

function AppContent() {
  return (
    <>
      <AuthCallbackHandler />
      <div className="App">
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/buyers" element={<ForBuyers />} />
          <Route path="/suppliers" element={<ForSuppliers />} />
          <Route path="/clients" element={<OurClients />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/claim/:id" element={<ClaimProfile />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/company/:id" element={<CompanyProfile />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/investor-relations" element={<InvestorRelations />} />
        </Routes>
        <Footer />
      </div>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <Analytics />
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
